import type { List } from '@/utils/list';

export enum NotificationDomain {
  COMMUNICATION = 'COMMUNICATION',
  VESSEL = 'VESSEL',
  ACTIVITY = 'ACTIVITY',
  PEOPLE = 'PEOPLE',
  SERVICE_PROVIDER = 'SERVICE-PROVIDER',
}

export enum NotificationCategory {
  CHAT = 'chat',
  VESSEL = 'vessel',
  ACTIVITY = 'activity',
  USER = 'user',
  SERVICE_PROVIDER = 'service_provider',
}

/**
 * Cross-service grouping set by the producing service (`event.subcategory`). Filtering by it
 * alone matches across every category, e.g. `messages` covers both chat services.
 */
export enum NotificationSubcategory {
  GENERAL = 'general',
  MESSAGES = 'messages',
  MENTIONS = 'mentions',
  MEMBERSHIP = 'membership',
  TASKS = 'tasks',
  EVENTS = 'events',
  MENU = 'menu',
  PAYMENTS = 'payments',
  SUBSCRIPTIONS = 'subscriptions',
  ROLES = 'roles',
  INVITES = 'invites',
  REFERRALS = 'referrals',
  OWNERSHIP = 'ownership',
}

export enum NotificationType {
  // COMMUNICATION
  NEW_MESSAGE = 'new_message',
  // A message that @-mentions the receiver is delivered under its own type instead of
  // `new_message` (the communication service swaps the type per recipient in
  // `dispatchNotifications`); the payload is the same `NewMessageEventData`.
  MENTION_MESSAGE = 'mention_message',
  // Persisted "added to / removed from chat" notifications carry the `_personal`
  // suffix (the plain `assign_user`/`unassign_user` are live-only WS events).
  ASSIGN_USER_PERSONAL = 'assign_user_personal',
  UNASSIGN_USER_PERSONAL = 'unassign_user_personal',

  // VESSEL
  DELETE_VESSEL = 'delete_vessel',
  // Every chef-menu mutation (menu or dish, created/updated/deleted) is sent under this
  // single type; the concrete change is in `data.action`.
  MENU_UPDATED = 'menu_updated',

  // ACTIVITY
  UPCOMING_EVENT = 'upcoming_event',
  TASK_REMINDER = 'task_reminder',
  ADDED_AS_PARTICIPANT_TASK = 'added_as_participant_task',
  ADDED_AS_SUPERVISOR = 'added_as_supervisor',
  ADDED_AS_RESPONSIBLE = 'added_as_responsible',
  ADDED_AS_PARTICIPANT_EVENT = 'added_as_participant_event',
  TASK_UPDATED = 'task_updated',
  EVENT_UPDATED = 'event_updated',
  TASK_DELETED = 'task_deleted',
  // A canceled event keeps its place in the calendar and can be renewed later.
  EVENT_CANCELED = 'event_canceled',
  EVENT_RENEWED = 'event_renewed',
  REMOVED_FROM_TASK = 'removed_from_task',
  REMOVED_FROM_EVENT = 'removed_from_event',
  REPEATABLE_TASKS_CREATED = 'repeatable_tasks_created',
  REPEATABLE_EVENTS_CREATED = 'repeatable_events_created',

  // PEOPLE
  ROLES_ASSIGNED = 'roles_assigned',
  ROLES_REMOVED = 'roles_removed',
  INVITE_CREATED = 'invite_created',
  INVITE_DECLINED = 'invite_declined',
  // When an invitation is withdrawn, the notification service rewrites the invitee's
  // `invite_created` row into this type in place (fresh date, unread) and delivers it like a new
  // one: over the socket and on the email/SMS/push channels the invitee's settings allow.
  INVITE_CANCELED = 'invite_canceled',
  REFERRAL_BONUS_APPLIED = 'referral_bonus_applied',
  // Multi-owner (people `feature/co-owner`). Sent to both sides of a handover; `recipient_role`
  // picks which wording the backend renders.
  OWNERSHIP_TRANSFERRED = 'ownership_transferred',
  // The owner left a vessel with no co-owners; sent to its captains, who now run it.
  VESSEL_OWNER_LEFT = 'vessel_owner_left',
  // Temporary crew. Expiring and ended go to the owner group and captains; updated, converted
  // and scheduled (an invitation accepted ahead of its start) to the member whose contract it is.
  CREW_CONTRACT_EXPIRING = 'crew_contract_expiring',
  CREW_CONTRACT_ENDED = 'crew_contract_ended',
  CREW_CONTRACT_UPDATED = 'crew_contract_updated',
  CREW_CONTRACT_CONVERTED = 'crew_contract_converted',
  CREW_CONTRACT_SCHEDULED = 'crew_contract_scheduled',

  // SERVICE-PROVIDER
  DELETE_SERVICE_PROVIDER = 'delete_service_provider',

  // SYSTEM
  FAILED_TO_SEND_EMAIL = 'failed_to_send_email',

  // WALLET
  PAYMENT_FAILED = 'payment_failed',
  // A checkout the payer abandoned or let expire. The notification service currently delivers
  // it as `payment_failed` carrying `status: "canceled"` and only swaps the message template
  // (`paymentText`, `pkg/dto/ws.go`), so both shapes are handled and read identically.
  PAYMENT_CANCELED = 'payment_canceled',
  PAYMENT_COMPLETED = 'payment_completed',
  PAYMENT_NEEDS_APPROVAL = 'payment_needs_approval',
  PAYOUT_LINK_CREATED = 'payout_link_created',
  PAYOUT_EXPIRED = 'payout_expired',
  PAYOUT_FAILED = 'payout_failed',

  // SUBSCRIPTION
  SUBSCRIPTION_UPDATED = 'subscription_updated',
  SUBSCRIPTION_ACTIVATED = 'subscription_activated',
  SUBSCRIPTION_TRIAL_GRANTED = 'subscription_trial_granted',
  SUBSCRIPTION_TRIAL_ENDING = 'subscription_trial_ending',
  SUBSCRIPTION_PAYMENT_FAILED = 'subscription_payment_failed',
  SUBSCRIPTION_DOWNGRADED = 'subscription_downgraded',
  SUBSCRIPTION_CANCELED = 'subscription_canceled',
  SUBSCRIPTION_RESUMED = 'subscription_resumed',
  // Whoever paid for the plan left the vessel, so it was cancelled at period end; sent to the
  // owner group (or the captains) so somebody takes it over.
  SUBSCRIPTION_PAYER_LEFT = 'subscription_payer_left',
  // A co-owner is left without a management seat after a plan change, so some rights lapse.
  CO_OWNER_SEAT_UNAVAILABLE = 'co_owner_seat_unavailable',
  /** The seat bought for somebody joining did not go through, so they are on Harbor. */
  SEAT_PAYMENT_FAILED = 'seat_payment_failed',
}

// Event data types

/** Payload of both `new_message` and `mention_message` notifications. */
export interface NewMessageEventData {
  tid: string;
  message: {
    id?: string;
    text?: string;
    room_id: string;
    creator_id?: string;
  };
  room_name: string;
  attachments: null | string[];
}

/**
 * Payload of the **live-only** `assign_user`/`unassign_user` WS events: the ids of the users
 * added to or removed from the room. Not the shape of the persisted `*_personal` notification —
 * see `AssignUserPersonalEventData`.
 */
export type AssignUserEventData = Array<string>;

/**
 * Payload of the persisted `assign_user_personal`/`unassign_user_personal` notifications. The
 * room is named at the top level, not under `message` the way a chat message carries it
 * (notification service `chatText`, `pkg/dto/ws.go`, and `buildChatLink`, `links.go`).
 */
export interface AssignUserPersonalEventData {
  room_id?: string;
  room_name?: string;
}

export interface DeleteVesselEventData {
  vessel_id: string;
  vessel_name: string;
}

/** Concrete change carried by a `menu_updated` notification. */
export enum MenuNotificationAction {
  CREATED = 'menu_created',
  UPDATED = 'menu_updated',
  DELETED = 'menu_deleted',
}

export interface MenuNotificationDish {
  id: string;
  name: string;
  type?: string;
  count?: number;
  description?: string;
}

/**
 * Sent to every participant of the event whose menu changed. Dish-level edits
 * (add/update/delete) arrive as `menu_updated` with the full menu snapshot, so
 * `menu.dishes` always reflects the state *after* the change.
 */
export interface MenuUpdatedEventData {
  vessel_id: string;
  event_id: string;
  action: MenuNotificationAction;
  menu: {
    id: string;
    event_id?: string;
    date?: string;
    meal_type?: string;
    dishes?: MenuNotificationDish[] | null;
  };
}

export interface UpcomingEventData {
  id: string;
  title: string;
  type: string;
  start: string;
  end: string;
}

export interface TaskReminderData {
  id: string;
  title: string;
  type: string;
  end: string;
}

export interface ParticipantData {
  id: string;
  title: string;
}

export interface TaskUpdateData {
  id: string;
  title: string;
}

export interface EventUpdateData {
  id: string;
  title: string;
}

export interface TaskDeleteData {
  id: string;
  title: string;
}

/**
 * Sent to every participant when an event (and all of its sub-events) is canceled. The event
 * is kept in place, so `status` is always "canceled" and the record stays navigable.
 * `cancel_reason` is optional and is not stored on the event — the backend omits the field
 * when the sender gave no reason, and that absence is what picks the `event_canceled` copy
 * over `event_canceled_reason`.
 */
export interface EventCanceledData {
  id: string;
  title: string;
  status: string;
  cancel_reason?: string;
}

/**
 * Sent when a canceled event is put back. The backend re-derives the status from the event's
 * time slot instead of restoring the previous one, so it can come back as planned, underway or
 * completed depending on when it was renewed — which is why the copy names the status.
 */
export interface EventRenewedData {
  id: string;
  title: string;
  status: string;
}

export interface RemovedFromTaskData {
  id?: string;
  title: string;
}

export interface RemovedFromEventData {
  id?: string;
  title: string;
}

/** Emitted when the recurring generator creates a batch of tasks/events. */
export interface RepeatableCreatedData {
  ids: string[];
  count: number;
  title: string;
}

export interface RolesAssignData {
  roles: string[];
  service_id: string;
}

/** Emitted when one or more roles are revoked from the user. */
export interface RolesRemovedData {
  role: string[] | string;
}

export interface DeleteServiceProviderEventData {
  service_provider_id: string;
  service_provider_name: string;
}

export interface InviteDeclinedData {
  role: string;
  name: string;
}

/**
 * `invite_canceled`: the invite snapshot people sent with `invite_created` (`InviteToStructDB`),
 * `id` being the withdrawn invite. An event invitation carries the event; `service_name` and
 * `event_name` are omitted when they could not be resolved.
 */
export interface InviteCanceledData {
  id: string;
  person_id: string;
  service_id: string;
  service_type: string;
  role: string;
  service_name?: string;
  event_id?: string;
  event_name?: string;
}

/**
 * Sent to the *referrer* once someone signs up with their code and the bonus is applied.
 * `joined_name` is omitted when the new user registered without a first/last name.
 */
export interface ReferralBonusAppliedData {
  free_months: number;
  joined_name?: string;
}

/** Wallet payment/payout notifications. `amount` is in cents. */
export interface PaymentEventData {
  amount: number;
  /**
   * Present on `payment_failed`; "canceled" means the checkout was canceled/expired, which is
   * what makes the backend render the `payment_canceled` copy instead of `payment_failed`.
   */
  status?: string;
  /**
   * Expense category the payment was filed under. The backend omits it from the wording when
   * unset or equal to its "other" default (`expensesCategory` in `pkg/dto/helpers.go`).
   */
  expenses_category?: string;
}

/**
 * Every `subscription_*` notification carries the same snapshot of the subscription
 * (people service, `SubscriptionResponse.ToStructPB`). The optional timestamps are RFC3339
 * and omitted when stripe has no value for them, which is what picks the `_no_date` /
 * `_period_end` copy variants on the backend.
 */
export interface SubscriptionEventData {
  id: string;
  service_id: string;
  tier: string;
  interval: string;
  seats: number;
  crew_seats: number;
  payment_status: string;
  cancel_at_period_end: boolean;
  trial_ends_at?: string;
  next_payment_at?: string;
  /** Set once the cancellation is scheduled; the subscription still runs until then. */
  cancel_at?: string;
  /** Set once the cancellation took effect. */
  canceled_at?: string;
}

/** `ownership_transferred`. Names are omitted when the backend could not resolve them. */
export interface OwnershipTransferredData {
  service_id: string;
  vessel_name?: string;
  from_person_id: string;
  from_name?: string;
  to_person_id: string;
  to_name?: string;
  /** `owner` for the person who took it over, `co-owner` for the one who handed it on. */
  recipient_role: 'owner' | 'co-owner';
}

/** `vessel_owner_left`, sent to each captain of a vessel left without an owner. */
export interface VesselOwnerLeftData {
  service_id: string;
  vessel_name?: string;
  owner_name?: string;
}

/** `subscription_payer_left`: the subscription snapshot plus who stopped paying. */
export interface SubscriptionPayerLeftData extends SubscriptionEventData {
  vessel_name?: string;
  /** Empty when the backend could not resolve the name. */
  payer_name?: string;
  /** RFC 3339; when the cancelled plan stops. Omitted when it has no end date. */
  ends_at?: string;
}

/** One co-owner left without a management seat. `name` is empty when it could not be resolved. */
export interface UnseatedCoOwner {
  id: string;
  name: string;
  seat_type: string;
}

/** `co_owner_seat_unavailable`: every co-owner on the vessel without a management seat, in one go. */
export interface CoOwnerSeatUnavailableData {
  service_id: string;
  vessel_name?: string;
  co_owners: UnseatedCoOwner[];
}

export type NotificationEventData =
  | NewMessageEventData
  | AssignUserEventData
  | AssignUserPersonalEventData
  | DeleteVesselEventData
  | MenuUpdatedEventData
  | UpcomingEventData
  | TaskReminderData
  | ParticipantData
  | TaskUpdateData
  | EventUpdateData
  | TaskDeleteData
  | EventCanceledData
  | EventRenewedData
  | RemovedFromTaskData
  | RemovedFromEventData
  | RepeatableCreatedData
  | RolesAssignData
  | RolesRemovedData
  | DeleteServiceProviderEventData
  | InviteDeclinedData
  | InviteCanceledData
  | ReferralBonusAppliedData
  | PaymentEventData
  | SubscriptionEventData
  | OwnershipTransferredData
  | VesselOwnerLeftData
  | SubscriptionPayerLeftData
  | CoOwnerSeatUnavailableData;

export interface NotificationEvent {
  domain: NotificationDomain;
  type: NotificationType;
  category: NotificationCategory;
  subcategory?: NotificationSubcategory;
  data: NotificationEventData;
  /**
   * Title of the notification, rendered by the notification service for this channel
   * (`Event.Render(..., ChannelFrontend)`, `pkg/dto/ws.go`) from the `<type>.frontend.subject`
   * template, falling back to "<Type Title Cased> notification". Absent on rows persisted
   * before the service started sending it, so the frontend keeps a per-type fallback.
   */
  subject?: string;
  /** Body text for this channel — the `<type>.frontend.body` template, else the shared one. */
  body?: string;
  /**
   * Absolute URL the notification points at, built by `links.BuildFrontendLink`
   * (`internal/app/links/links.go`). Covers chat, task, event and payment types only;
   * empty for everything else.
   */
  link?: string;
}

// Per docs, the API serializes `event` as `[]byte` (swagger reports `array of integer`).
// In practice servers may emit it as a JSON object, a base64 string, or a byte array,
// so we accept all three shapes here and normalize in the mapper.
export type NotificationEventPayload = NotificationEvent | number[] | string;

export interface NotificationDto {
  id: string;
  receiver: string;
  event: NotificationEventPayload;
  read: boolean;
  date: string;
  sender_id?: string;
  sender_role?: string;
}

export type NotificationPageDto = List<NotificationDto> & { unread: number };

export interface Notification {
  id: string;
  receiver: string;
  /** Null when the `event` blob did not decode — the notification then renders as unavailable. */
  event: NotificationEvent | null;
  read: boolean;
  date: string;
  senderId?: string;
  senderRole?: string;
}

export type NotificationPage = List<Notification> & { unread: number };

export interface ReadNotificationsDto {
  notification_ids: string[];
}

export interface DeleteNotificationsDto {
  notification_ids: string[];
}

export interface NotificationsListFilters {
  domain?: NotificationDomain;
  type?: NotificationType;
  category?: NotificationCategory;
  subcategory?: NotificationSubcategory;
  only_unread?: boolean;
  /** RFC3339 UTC date, e.g. "2026-05-20T00:00:00Z" */
  date_from?: string;
  /** Filter by sender role (owner, guest, captain, chef, mate, steward, service-provider) */
  role_priority?: string;
}
